<template>
  <div>
    <b-form-group
      :label="`${$t('reusable.common.name')}:`"
      label-for="input-status-name"
      label-cols="4"
      label-cols-sm="3"
      class="label"
    >
      <b-form-input
        id="input-status-name"
        v-model="$v.status_form.name.$model"
        :state="validateState('name')"
        size="sm"
      ></b-form-input>
      <b-form-invalid-feedback>
        {{ $t("reusable.common.minLength", { min: 3 }) }}
      </b-form-invalid-feedback>
    </b-form-group>

    <b-form-group
      :label="`${$t('teaching.instructionalMethod.statusOrder')}:`"
      label-for="input-status-order"
      label-cols="4"
      label-cols-sm="3"
      class="label"
    >
      <b-form-input
        id="input-status-order"
        v-model="$v.status_form.order.$model"
        :state="validateState('order')"
        type="number"
        min="1"
        size="sm"
      ></b-form-input>
      <b-form-invalid-feedback>
        {{ $t("teaching.instructionalMethod.statusOrderInvalid") }}
      </b-form-invalid-feedback>
      <b-form-text>
        {{ $t("teaching.instructionalMethod.statusOrderHint") }}
      </b-form-text>
    </b-form-group>

    <!-- El color viaja con el estado: la insignia que lo pinta no puede tener una
         tabla de colores propia si los estados dejan de ser cinco fijos. -->
    <b-form-group
      :label="`${$t('teaching.instructionalMethod.statusColor')}:`"
      label-for="input-status-color"
      label-cols="4"
      label-cols-sm="3"
      class="label"
    >
      <div class="d-flex align-items-center">
        <b-form-input
          id="input-status-color"
          v-model="status_form.color"
          type="color"
          size="sm"
          class="kl-color-input"
        ></b-form-input>
        <span class="kl-status-preview ml-2" :style="previewStyle">
          {{ status_form.name || $t("teaching.instructionalMethod.statusEntity") }}
        </span>
      </div>
    </b-form-group>

    <b-form-group
      label-cols="4"
      label-cols-sm="3"
      :label="`${$t('teaching.instructionalMethod.statusActive')}:`"
      class="label"
    >
      <b-form-checkbox v-model="status_form.is_active" switch>
        <span class="kl-hint">
          {{ $t("teaching.instructionalMethod.statusActiveHint") }}
        </span>
      </b-form-checkbox>
    </b-form-group>

    <div class="row mt-3">
      <div class="col" style="text-align: right">
        <b-button size="sm" :disabled="saving" @click="save">
          {{ $t("reusable.common.save") }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";
import { validationMixin } from "vuelidate";
import { required, minLength, numeric } from "vuelidate/lib/validators";

export default {
  name: "InstructionalStatusForm",
  mixins: [validationMixin],
  props: {
    // null => creación.
    InstructionalStatus: {
      type: Object,
      default: null,
    },
    // Orden sugerido al crear: se ubica al final de los existentes.
    next_order: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      status_form: {
        name: this.InstructionalStatus ? this.InstructionalStatus.name : "",
        order: this.InstructionalStatus
          ? this.InstructionalStatus.order
          : this.next_order,
        color:
          this.InstructionalStatus && this.InstructionalStatus.color
            ? this.InstructionalStatus.color
            : "#6b7b79",
        is_active: this.InstructionalStatus
          ? this.InstructionalStatus.is_active
          : true,
      },
      saving: false,
    };
  },
  validations: {
    status_form: {
      name: { required, minLength: minLength(3) },
      order: {
        required,
        numeric,
        positive: (value) => Number(value) >= 1,
      },
    },
  },
  computed: {
    ...mapGetters({
      institution: "getInstitution",
    }),
    previewStyle() {
      const color = this.status_form.color || "#6b7b79";
      return {
        backgroundColor: color,
        color: this.$lightOrDark(color) ? "#2b2a2a" : "#ffffff",
      };
    },
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.status_form[name];
      return $dirty ? !$error : null;
    },
    save() {
      this.$v.status_form.$touch();
      if (this.$v.status_form.$anyError) return;
      this.saving = true;
      const payload = {
        name: this.status_form.name.trim(),
        order: Number(this.status_form.order),
        color: this.status_form.color,
        is_active: this.status_form.is_active,
      };
      const request = this.InstructionalStatus
        ? this.$store.dispatch(names.PATCH_INSTRUCTIONAL_STATUS, {
            instructional_status_id: this.InstructionalStatus.id,
            item: payload,
          })
        : this.$store.dispatch(names.POST_INSTRUCTIONAL_STATUS, {
            ...payload,
            school: this.institution ? this.institution.id : null,
          });

      request
        .then((response) => {
          this.saving = false;
          toast(this.$t("teaching.instructional.saved"));
          this.$emit("saved", response);
        })
        .catch(() => {
          this.saving = false;
          toast(this.$t("teaching.instructional.saveError"));
        });
    },
  },
};
</script>

<style scoped>
.kl-color-input {
  width: 4rem;
  padding: 0.1rem 0.2rem;
}
/* Se ve el estado tal como va a quedar en la insignia, con el texto ya contrastado. */
.kl-status-preview {
  font-size: var(--kl-fs-caption);
  font-weight: var(--kl-chip-fw, 600);
  border-radius: var(--kl-radius-pill, 999px);
  padding: 0.15rem 0.6rem;
  letter-spacing: 0.03em;
}
.kl-hint {
  font-size: var(--kl-fs-small);
  color: #6b7b79;
}
</style>
